import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Terms from './components/Terms';
import Privacy from './components/Privacy';
import Support from './components/Support';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import Onboarding from './components/Onboarding';
import InfluencerLogin from './components/InfluencerLogin';
import InfluencerDashboard from './components/InfluencerDashboard';
import HowItWorksPage from './components/HowItWorksPage';
import Footer from './components/Footer';
import LandingPage from './components/LandingPage';
import Blog from './components/Blog';
import BlogPost from './components/BlogPost';
import './App.css';

// Componente para proteger rutas
function PrivateRoute({ children }) {
  const { currentUser, userProfile, loading } = useAuth();
  
  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }
  
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  
  // Si el usuario no ha completado el onboarding, redirigir al onboarding
  // EXCEPCIÓN: Si hay parámetros de pago exitoso en la URL, permitir acceso al Dashboard
  const urlParams = new URLSearchParams(window.location.search);
  const paymentStatus = urlParams.get('payment');
  const hasPaymentSuccess = paymentStatus === 'success';
  
  // Si el perfil está cargado y no completó onboarding, redirigir
  if (userProfile && !userProfile.onboardingCompletado && !hasPaymentSuccess) {
    return <Navigate to="/onboarding" />;
  }
  
  // Si el perfil aún no está cargado, mostrar loading
  if (!userProfile && !hasPaymentSuccess) {
    return <div className="loading-screen">Loading profile...</div>;
  }
  return children;
}

// Componente para rutas públicas (solo si no está autenticado)
function PublicRoute({ children }) {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }
  
  return !currentUser ? children : <Navigate to="/dashboard" />;
}

// Componente para el onboarding (solo si está autenticado pero no completó onboarding)
function OnboardingRoute({ children }) {
  const { currentUser, userProfile, loading } = useAuth();
  
  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }
  
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  
  // Si ya completó el onboarding, redirigir al dashboard
  if (userProfile && userProfile.onboardingCompletado) {
    return <Navigate to="/dashboard" />;
  }
  
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route 
        path="/terms" 
        element={<Terms />} 
      />
      <Route 
        path="/privacy" 
        element={<Privacy />} 
      />
      <Route 
        path="/support" 
        element={<Support />} 
      />
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        } 
      />
      <Route 
        path="/onboarding" 
        element={
          <OnboardingRoute>
            <Onboarding />
          </OnboardingRoute>
        } 
      />
      <Route 
        path="/dashboard" 
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        } 
      />
      <Route 
        path="/influencer/login" 
        element={<InfluencerLogin />} 
      />
      <Route 
        path="/influencer/dashboard" 
        element={<InfluencerDashboard />} 
      />
      <Route 
        path="/how-it-works" 
        element={<HowItWorksPage />} 
      />
      <Route 
        path="/blog" 
        element={<Blog />} 
      />
          <Route
            path="/blog/:id"
            element={<BlogPost />}
          />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <LanguageProvider>
        <AuthProvider>
          <div className="App">
            <AppRoutes />
            <Footer />
          </div>
        </AuthProvider>
      </LanguageProvider>
    </Router>
  );
}

export default App;
